import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../Components/Footer';
import Auth from '../../Function/Auth';
import Header from '../../Components/Header';
import { toast, ToastContainer } from "react-toastify";

function Vendor() {
    const authToken = sessionStorage.getItem('authToken');
    const baseurl = process.env.REACT_APP_BASE_API_URL;

    const [analytics, setAnalytics] = useState({});
    const [vendorData, setVendorData] = useState([]);
    const [pagination, setPagination] = useState({});
    const [search, setSearch] = useState("");
    const [tableLoading, setTableLoading] = useState(false);

    // Add Vendor Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        sponsor_id: "",
        password: "",
        locality: "",
        city: "",
        district: "",
        state: "",
        pincode: ""
    });

    useEffect(() => {
        fetchVendorData(1);
    }, []);

    const fetchVendorData = async (page = 1) => {
        setTableLoading(false);
        try {
            const response = await fetch(`${baseurl}/franchise/data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ search, page }),
            });

            const result = await response.json();

            if (result.status) {
                setAnalytics(result.franchiseCount || {});
                setVendorData(result.data || []);
                setPagination(result.pagination || {});
                setTableLoading(false);
            } else {
                toast.error(result.message || "Failed to load data");
            }
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
        } finally {
            setTableLoading(true);
        }
    };

    const handlePageChange = (page) => {
        if (page < 1 || (pagination.last_page && page > pagination.last_page)) return;
        fetchVendorData(page);
    };

    const searchData = () => fetchVendorData(1);

    // Handle Input Change
    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Add New Franchise
    const handleAddVendor = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const response = await fetch(`${baseurl}/franchise/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || "Franchise created successfully");
                setShowAddModal(false);
                resetForm();
                fetchVendorData(1); // Refresh list
            } else {
                if (result.errors) {
                    // Show validation errors
                    Object.values(result.errors).forEach(errArray => {
                        errArray.forEach(err => toast.error(err));
                    });
                } else {
                    toast.error(result.message || "Failed to create franchise");
                }
            }
        } catch (error) {
            console.error(error);
            toast.error("Server error occurred");
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            sponsor_id: "",
            password: "",
            locality: "",
            city: "",
            district: "",
            state: "",
            pincode: ""
        });
    };

    const deleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this vendor?")) return;

        try {
            const response = await fetch(`${baseurl}/delete-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ user_id: userId }),
            });

            const data = await response.json();

            if (data.status) {
                toast.success(data.message);
                fetchVendorData(pagination.current_page || 1);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error("Failed to delete vendor");
        }
    };

    return (
        <>
            <Header>
                <Auth />
                <div className="main-content app-content">
                    <div className="container-fluid">

                        {/* Page Header */}
                        <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                            <h4 className="mb-0 fw-bold">Vendor Management</h4>

                            <button className="btn btn-primary shadow" onClick={() => setShowAddModal(true)}>
                                <i className="fa fa-plus me-2"></i>
                                Add Vendor
                            </button>
                        </div>

                        {/* Analytics Cards */}
                        <div className="row mb-4">
                            {[
                                { title: "TOTAL VENDOR", value: analytics?.total || 0, color: "bg-primary-gradient" },
                                { title: "ACTIVE VENDOR", value: analytics?.active_franchise || 0, color: "bg-success-gradient" },
                                { title: "PENDING VENDOR", value: analytics?.pending_franchise || 0, color: "bg-warning-gradient" },
                                { title: "HOLD VENDOR", value: analytics?.hold_franchise || 0, color: "bg-danger-gradient" },
                            ].map((item, i) => (
                                <div key={i} className="col-xl-3 col-lg-6 col-md-6">
                                    <div className={`card sales-card ${item.color}`}>
                                        <div className="px-3 pt-3 pb-2">
                                            <h6 className="mb-3 fs-12 text-fixed-white">{item.title}</h6>
                                            <h4 className="fs-20 fw-bold text-fixed-white">{item.value}</h4>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="card card-body mb-4">
                            <div className="row align-items-end">
                                <div className="col-md-9">
                                    <label className="form-label">Search Vendor</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by Franchise ID, User ID, Sponsor ID, City, State..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && searchData()}
                                    />
                                </div>
                                <div className="col-md-3 mt-2">
                                    <button onClick={searchData} className="btn btn-primary w-100">Search</button>
                                </div>
                            </div>
                        </div>

                        {/* Vendor Table */}
                        <div className="row">
                            <div className="col-md-12 col-lg-12 col-xl-12">
                                <div className="card card-table">
                                    <div className=" card-header p-0 d-flex justify-content-between mt-3">
                                        <h3 className="card-title mb-1 text-primary">User Data</h3>
                                    </div>
                                    <div className="table-responsive country-table">
                                        {tableLoading ? (
                                            <table className="table table-striped table-bordered mb-0 text-nowrap mt-3">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Franchise ID</th>
                                                        <th>User ID</th>
                                                        <th>Sponsor ID</th>
                                                        <th>Location</th>
                                                        <th>Status</th>
                                                        <th>Withdraw</th>
                                                        <th>Created</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>

                                                <tbody>

                                                    {vendorData.length > 0 ? (
                                                        vendorData.map((vendor, index) => (

                                                            <tr key={vendor.id}>

                                                                <td>{index + 1}</td>

                                                                <td>
                                                                    <b>{vendor.franchise_id}</b>
                                                                </td>

                                                                <td>{vendor.user_id}</td>

                                                                <td>{vendor.sponsor_id}</td>

                                                                <td>
                                                                    {vendor.locality},
                                                                    <br />
                                                                    {vendor.city},
                                                                    <br />
                                                                    {vendor.state}
                                                                </td>

                                                                <td>

                                                                    <span
                                                                        className={`badge ${vendor.status === "active"
                                                                            ? "bg-success"
                                                                            : vendor.status === "pending"
                                                                                ? "bg-warning"
                                                                                : "bg-danger"
                                                                            }`}
                                                                    >
                                                                        {vendor.status}
                                                                    </span>

                                                                </td>

                                                                <td>

                                                                    <span
                                                                        className={`badge ${vendor.withdraw_status === "active"
                                                                            ? "bg-success"
                                                                            : "bg-warning"
                                                                            }`}
                                                                    >
                                                                        {vendor.withdraw_status}
                                                                    </span>

                                                                </td>

                                                                <td>
                                                                    {new Date(
                                                                        vendor.created_at
                                                                    ).toLocaleString()}
                                                                </td>

                                                                <td>
                                                                    <Link to={`/vendor-details`} onClick={() => { sessionStorage.setItem("vendor_id", vendor.id); }} className="btn btn-sm btn-primary">
                                                                        <i className="fa fa-eye"></i>
                                                                    </Link>
                                                                </td>

                                                            </tr>

                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="9" className="text-center">
                                                                No Vendor Found
                                                            </td>
                                                        </tr>
                                                    )}

                                                </tbody>
                                            </table>
                                        ) : (
                                            <div className="text-center">
                                                <div class="d-flex justify-content-center mb-4">
                                                    <div class="spinner-border" role="status">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}


                                    </div>
                                    <div class="gridjs-footer">
                                        <div class="gridjs-pagination">
                                            <div role="status" aria-live="polite" class="gridjs-summary" title="Page 1 of 1">Showing <b>{pagination.current_page}</b> to <b>{pagination.per_page}</b> of <b>{pagination.total}</b> results</div>
                                            <div className="gridjs-pages text-end">
                                                {/* Previous Button */}
                                                <button tabIndex="0" role="button" title="Previous" aria-label="Previous" className={pagination.current_page === 1 ? "disabled" : ""} onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page === 1}> Previous</button>

                                                {/* First Page */}
                                                <button tabIndex="0" role="button" className={` ${pagination.current_page === 1 ? "gridjs-currentPage" : ""}`} title="Page 1" aria-label="Page 1" onClick={() => handlePageChange(1)}> 1</button>

                                                {/* Ellipsis if needed */}
                                                {pagination.current_page > 3 && <span>...</span>}

                                                {/* Middle Pages (2 dynamic pages around current) */}
                                                {Array.from({ length: 5 }, (_, i) => {
                                                    const page = pagination.current_page - 2 + i;
                                                    if (page > 1 && page < pagination.last_page) {
                                                        return (
                                                            <button key={page} tabIndex="0" role="button" className={` ${pagination.current_page === page ? "gridjs-currentPage" : ""}`} title={`Page ${page}`}
                                                                aria-label={`Page ${page}`} onClick={() => handlePageChange(page)}>
                                                                {page}
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })}

                                                {/* Ellipsis if needed */}
                                                {pagination.current_page < pagination.last_page - 2 && <span>...</span>}

                                                {/* Last Page */}
                                                {pagination.last_page > 1 && (
                                                    <button tabIndex="0" role="button" className={` ${pagination.current_page === pagination.last_page ? "gridjs-currentPage" : ""}`} title={`Page ${pagination.last_page}`} aria-label={`Page ${pagination.last_page}`}
                                                        onClick={() => handlePageChange(pagination.last_page)}>
                                                        {pagination.last_page}
                                                    </button>
                                                )}

                                                {/* Next Button */}
                                                <button tabIndex="0" role="button" title="Next" aria-label="Next" className={pagination.current_page === pagination.last_page ? "disabled" : ""} onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page}>
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <ToastContainer />
            </Header>
            <Footer />

            {/* ==================== Add Vendor Modal ==================== */}
            {showAddModal && (
                <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Franchise / Vendor</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
                            </div>

                            <form onSubmit={handleAddVendor}>
                                <div className="modal-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Sponsor ID</label>
                                            <input type="text" className="form-control" name="sponsor_id" value={formData.sponsor_id} onChange={handleInputChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Password <span className="text-danger">*</span></label>
                                            <input type="password" className="form-control" name="password" value={formData.password} onChange={handleInputChange} required />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label">Locality <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="locality" value={formData.locality} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">City <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="city" value={formData.city} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">District <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="district" value={formData.district} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">State <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="state" value={formData.state} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Pincode <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" name="pincode" value={formData.pincode} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                                        {submitting ? "Creating..." : "Create Franchise"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Vendor;